import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Candidate } from "@/types/candidate";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";
import { toast } from "@/hooks/use-toast";
import { normalizeSkill } from "@/utils/skillNormalization";
import { useAuth } from "@/providers/AuthProvider";

const ITEMS_PER_PAGE = 25;

export interface CandidateFilters {
	searchTerm?: string;
	skillsFilter?: string[];
	skillsOperator?: "AND" | "OR";
	statusFilter?: string[];
	functionFilter?: string;
	assignedToMe?: boolean;
	currentUserId?: string | null;
	showFavoritesOnly?: boolean;
	favorites?: Set<string>;
}

export interface UseCandidatesPaginatedReturn {
	candidates: Candidate[];
	isLoading: boolean;
	totalCount: number;
	totalPages: number;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	refetch: () => Promise<void>;
}

export const useCandidatesPaginated = (
	filters: CandidateFilters,
	sortField: SortField = "dateCreated",
	sortOrder: SortOrder = "desc"
): UseCandidatesPaginatedReturn => {
	const [candidates, setCandidates] = useState<Candidate[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const { currentOrganization } = useAuth();
	const orgUuid = currentOrganization?.id ?? null;

	const fetchCandidates = useCallback(async () => {
		if (!orgUuid)
			return;

		try {
			setIsLoading(true);

			// Start building query - APPLY ALL FILTERS IN DATABASE
			let query = supabase
				.from('candidates')
				.select('id, name, email, phone, function, skills, match_score, status, location, default_resume, manager', { count: 'exact' })
				.eq('organization_uuid', orgUuid);

			// Favorites filter - apply in database first if enabled
			if (filters.showFavoritesOnly && filters.favorites && filters.favorites.size > 0) {
				const favoriteIds = Array.from(filters.favorites);
				query = query.in('id', favoriteIds);
			}

			// Status filter
			if (filters.statusFilter && filters.statusFilter.length > 0) {
				query = query.in('status', filters.statusFilter);
			}

			// Function filter (case-insensitive LIKE)
			if (filters.functionFilter && filters.functionFilter.trim() !== "") {
				query = query.ilike('function', `%${filters.functionFilter}%`);
			}

			// Assigned to me filter
			if (filters.assignedToMe && filters.currentUserId) {
				query = query.eq('manager', filters.currentUserId);
			}

			// Skills filter - fetch candidates with skills and match client-side for case-insensitive comparison
			if (filters.skillsFilter && filters.skillsFilter.length > 0) {
				const normalizedSkillsFilter = filters.skillsFilter.map(skill => normalizeSkill(skill));

				// Fetch all candidate IDs with their skills for this org
				const { data: candidatesWithSkills, error: skillsError } = await supabase
					.from('candidates')
					.select('id, skills')
					.eq('organization_uuid', orgUuid)
					.not('skills', 'is', null);

				if (skillsError) {
					console.error("Error fetching candidates for skill filtering:", skillsError);
				}

				const matchingCandidateIds: string[] = [];
				if (candidatesWithSkills) {
					for (const candidate of candidatesWithSkills) {
						if (!candidate.skills || candidate.skills.length === 0) continue;
						const normalizedCandidateSkills = candidate.skills.map((s: string) => normalizeSkill(s));

						const matches = filters.skillsOperator === "AND"
							? normalizedSkillsFilter.every(fs => normalizedCandidateSkills.includes(fs))
							: normalizedSkillsFilter.some(fs => normalizedCandidateSkills.includes(fs));

						if (matches)
							matchingCandidateIds.push(candidate.id);
					}
				}

				if (matchingCandidateIds.length > 0) {
					query = query.in('id', matchingCandidateIds);
				} else {
					// No matches found - return empty result
					query = query.in('id', ['00000000-0000-0000-0000-000000000000']);
				}
			}

			// Search term filter (searches name, email, location)
			if (filters.searchTerm && filters.searchTerm.trim() !== "") {
				const term = filters.searchTerm.trim();
				query = query.or(`name.ilike.%${term}%,email.ilike.%${term}%,location.ilike.%${term}%`);
			}

			// Sorting
			const sortMapping: Record<SortField, string> = {
				name: 'name',
				dateCreated: 'created_at',
				matchScore: 'match_score'
			};
			const dbSortField = sortMapping[sortField] || 'created_at';
			query = query.order(dbSortField, { ascending: sortOrder === 'asc' });

			// Pagination - AFTER all filters
			const from = (currentPage - 1) * ITEMS_PER_PAGE;
			query = query.range(from, from + ITEMS_PER_PAGE - 1);

			const { data, error, count } = await query;

			if (error) {
				throw error;
			}

			setCandidates(data || []);
			setTotalCount(count || 0);
		} catch (error) {
			console.error("Error fetching candidates:", error);
			toast({
				title: "Failed to load candidates",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
			setCandidates([]);
			setTotalCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		orgUuid,
		currentPage,
		filters.searchTerm,
		filters.skillsFilter,
		filters.skillsOperator,
		filters.statusFilter,
		filters.functionFilter,
		filters.assignedToMe,
		filters.currentUserId,
		filters.showFavoritesOnly,
		filters.favorites,
		sortField,
		sortOrder
	]);

	useEffect(() => {
		fetchCandidates();
	}, [fetchCandidates]);

	// Reset to page 1 when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		filters.searchTerm,
		filters.skillsFilter,
		filters.statusFilter,
		filters.functionFilter,
		filters.assignedToMe,
		filters.showFavoritesOnly
	]);

	const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

	return {
		candidates,
		isLoading,
		totalCount,
		totalPages,
		currentPage,
		setCurrentPage,
		refetch: fetchCandidates
	};
};
